import { cn } from '../lib/cn';

/**
 * Stüdyo markası.
 *
 * ## Amblem artık RESMÎ LOGO dosyası
 *
 * Önceden amblem CSS ile çizilmiş bir kutu içinde "ZEE" yazısıydı; markanın
 * gerçek logosu depoda duruyor ama hiçbir yerde kullanılmıyordu. Artık
 * `public/logo.png` render ediliyor — tek resmî işaret bu.
 *
 * O dosya kaynak `logo_1.png`'den ÜRETİLDİ, kopyalanmadı. Kaynağın iki kusuru
 * vardı: şeffaflık daması piksel olarak gömülüydü (köşeler opak gri, yani
 * dosya hiç şeffaf değildi) ve 4.8 MB'tı. Üretilen varlık gerçek alfa taşıyor,
 * iki markaya renge yuvarlanmış ve 28 kB.
 *
 * ## Koyu zeminde beyaz güvenli alan
 *
 * Marka mavisi (#1F4898) koyu kabuk renginin (#191713) üstünde 2.08:1 kontrast
 * veriyor — WCAG 1.4.11'in anlamlı grafikler için istediği 3:1'in altında,
 * yani logo koyu navbar'da eriyordu. Koyu zeminde logo beyaz bir disk içine
 * alınıyor; bu, markanın "safe area" kuralının da standart uygulaması.
 * Krem zeminde gerek yok: orada kontrast 8.2:1.
 *
 * ## Yazı rengi zemine bağlı
 *
 * Kelime markası bir zamanlar `text-white` olarak SABİT yazılmıştı ve krem
 * zeminde beyaz-üstüne-beyaz kalıyordu (1.05:1 — görünmüyordu). Renk artık
 * çağıranın beyan ettiği `tone`'a bağlı ve varsayılan `light`: yanlış kullanım
 * en kötü ihtimalle koyu zeminde koyu metin verir, yani fark edilir. Görünmez
 * bir hata, fark edilen bir hatadan pahalıdır.
 */

interface ZeeLogoProps {
  size?: 'sm' | 'md' | 'lg';
  /**
   * Kelime markası ("Zee Sports Studio"). Telefonda da görünür: bir süre
   * `sm` altında gizliydi ve üyeler logonun yanında stüdyonun adını
   * göremiyordu. Yer, üst çubuklardaki diğer öğeler daraltılarak açıldı
   * (panelde kredi rozeti kalktı, sitede dil düğmesi telefonda menüde).
   */
  showTextLabel?: boolean;
  /** Logonun üstünde durduğu zemin. Yazı rengini ve güvenli alanı bu belirler. */
  tone?: 'light' | 'dark';
  /** Alt başlık — tanıtım sitesi aktif dilde verir. */
  tagline?: string;
  className?: string;
}

/** Kutu ölçüsü ve intrinsic piksel — ikisi birlikte yerleşim kaymasını önler. */
const EMBLEM = {
  sm: { box: 'h-9 w-9', px: 36 },
  md: { box: 'h-11 w-11', px: 44 },
  lg: { box: 'h-14 w-14', px: 56 },
} as const;

const WORDMARK_SIZE = {
  sm: 'text-base',
  md: 'text-lg',
  lg: 'text-xl',
} as const;

export function ZeeLogo({
  size = 'md',
  showTextLabel = true,
  tone = 'light',
  tagline = 'Reformer & Fonksiyonel',
  className,
}: ZeeLogoProps) {
  const isDark = tone === 'dark';
  const emblem = EMBLEM[size];

  return (
    <span className={cn('flex select-none items-center gap-2.5', className)}>
      <img
        src="/logo.png"
        /* Kelime markası yanındaysa logo DEKORATİF: ekran okuyucu aynı adı iki
           kez seslendirmemeli. Tek başınaysa markanın tek taşıyıcısı. */
        alt={showTextLabel ? '' : 'Zee Sports Studio'}
        aria-hidden={showTextLabel || undefined}
        width={emblem.px}
        height={emblem.px}
        className={cn(
          'shrink-0 rounded-full object-contain',
          emblem.box,
          // Koyu zeminde güvenli alan — bkz. yukarıdaki kontrast gerekçesi.
          isDark && 'bg-white p-[3px]',
        )}
      />

      {showTextLabel && (
        <span
          className="flex min-w-0 flex-col leading-tight"
        >
          {/* `whitespace-nowrap`: 375px'te marka adı üç satıra sarıp 64px'lik
              çubuğu taşırıyordu. Alt başlık ise mobilde tamamen gizli — orada
              yer, kimliği tekrar etmekten daha değerli. */}
          <span
            className={cn(
              'whitespace-nowrap font-serif font-semibold tracking-tight',
              WORDMARK_SIZE[size],
              isDark ? 'text-shell-ink' : 'text-ink',
            )}
          >
            Zee Sports Studio
          </span>
          <span
            className={cn(
              'hidden whitespace-nowrap text-micro font-semibold uppercase tracking-[0.14em] sm:block',
              isDark ? 'text-shell-ink/60' : 'text-ink-3',
            )}
          >
            {tagline}
          </span>
        </span>
      )}
    </span>
  );
}
